import { Box, IconButton, Typography } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import NodeSelect from '../nodes/common/NodeSelect'
import { useAppTheme } from '@/theme/ThemeContext'
import {
  ALL_WEEKDAYS,
  WEEKDAY_LONG,
  WEEKDAY_SHORT,
  addRow,
  removeRow,
  rowText,
  setRow,
  toRows,
  type Calendar,
  type CalendarRow,
  type EditableRowHours,
  type Grain,
  type Weekday,
} from '@/alerts/calendar'

const clock = (h: number) => `${String(h % 24).padStart(2, '0')}:00`
const ALL_DAY = 'all'
const FROM_OPTIONS = [
  { id: ALL_DAY, label: 'כל היום' },
  ...Array.from({ length: 24 }, (_, h) => ({ id: String(h), label: clock(h) })),
]
// "to" is exclusive: 24 is midnight at the end of the day, shown as 00:00.
const TO_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  id: String(i + 1),
  label: clock(i + 1),
}))

/**
 * Edits a calendar as window rows. Controlled and stateless: every edit goes through `onChange`,
 * which gets undefined when the last row is removed. The editor lays itself out right to left
 * whatever the page direction is.
 */
const CalendarEditor = ({
  value,
  onChange,
  grain,
}: {
  value: Calendar
  onChange: (next: Calendar | undefined) => void
  grain: Grain
}) => {
  const { mode } = useAppTheme()
  const dark = mode === 'dark'
  const colors = {
    text: dark ? '#FFFFFF' : '#003F61',
    border: dark ? '#445876' : 'black',
    bg: dark ? '#0D192F' : 'transparent',
    onBg: dark ? '#E3F5FF' : '#003F61',
    onText: dark ? '#0D192F' : '#FFFFFF',
  }
  const textSx = {
    color: colors.text,
    fontFamily: 'Rubik, sans-serif',
    fontSize: '14px',
    fontWeight: 300,
  }

  const edit = (row: number, days: Weekday[], hours: EditableRowHours) => {
    const next = setRow(value, row, days, hours, grain)
    if (next !== value) onChange(next)
  }

  const renderRow = (row: CalendarRow, i: number) => {
    const hours = row.hours
    const removeButton = (
      <IconButton
        className="nodrag"
        size="small"
        aria-label={`מחיקת חלון ${i + 1}`}
        onClick={() => onChange(removeRow(value, i))}
        sx={{ color: colors.text, marginInlineStart: 'auto' }}
      >
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    )
    if (hours.kind === 'scattered') {
      return (
        <Box
          key={row.windows.join('-')}
          role="group"
          aria-label={`חלון ${i + 1}`}
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Typography sx={textSx}>{rowText(row)}</Typography>
          {removeButton}
        </Box>
      )
    }
    const toggle = (d: Weekday) =>
      edit(
        i,
        row.days.includes(d) ? row.days.filter(x => x !== d) : [...row.days, d],
        hours
      )
    const crosses = hours.kind === 'range' && hours.to <= hours.from
    return (
      <Box
        key={row.windows.join('-')}
        role="group"
        aria-label={`חלון ${i + 1}`}
        sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {ALL_WEEKDAYS.map(d => {
            const on = row.days.includes(d)
            return (
              <Box
                component="button"
                type="button"
                key={d}
                className="nodrag"
                aria-pressed={on}
                aria-label={WEEKDAY_LONG[d]}
                onClick={() => toggle(d)}
                sx={{
                  width: 36,
                  height: 36,
                  padding: 0,
                  borderRadius: '50%',
                  border: `1px solid ${on ? colors.onBg : colors.border}`,
                  background: on ? colors.onBg : colors.bg,
                  color: on ? colors.onText : colors.text,
                  fontFamily: 'Rubik, sans-serif',
                  fontSize: '13px',
                  fontWeight: on ? 500 : 300,
                  cursor: 'pointer',
                }}
              >
                {WEEKDAY_SHORT[d]}
              </Box>
            )
          })}
          {removeButton}
        </Box>
        {grain === 'hour' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={textSx}>משעה</Typography>
            <Box dir="ltr">
              <NodeSelect
                ariaLabel="משעה"
                placeholder="שעה"
                options={FROM_OPTIONS}
                value={hours.kind === 'all' ? ALL_DAY : String(hours.from)}
                onChangeValue={v => {
                  if (v === null) return
                  if (v === ALL_DAY) return edit(i, row.days, { kind: 'all' })
                  const from = Number(v)
                  edit(i, row.days, {
                    kind: 'range',
                    from,
                    to: hours.kind === 'range' ? hours.to : from + 1,
                  })
                }}
              />
            </Box>
            {hours.kind === 'range' && (
              <>
                <Typography sx={textSx}>עד</Typography>
                <Box dir="ltr">
                  <NodeSelect
                    ariaLabel="עד"
                    placeholder="שעה"
                    options={TO_OPTIONS}
                    value={String(hours.to)}
                    onChangeValue={v =>
                      v !== null &&
                      edit(i, row.days, {
                        kind: 'range',
                        from: hours.from,
                        to: Number(v),
                      })
                    }
                  />
                </Box>
                {crosses && <Typography sx={textSx}>למחרת</Typography>}
              </>
            )}
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box
      dir="rtl"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {toRows(value).map(renderRow)}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          component="button"
          type="button"
          className="nodrag"
          onClick={() => onChange(addRow(value))}
          sx={{
            ...textSx,
            fontWeight: 400,
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          + הוספת חלון
        </Box>
        <Typography sx={{ ...textSx, fontSize: '12px', opacity: 0.7 }}>
          לפי שעון ישראל
        </Typography>
      </Box>
    </Box>
  )
}

export default CalendarEditor
